import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('vf_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('vf_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem('vf_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.requires_2fa) {
      return { requires_2fa: true, email };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('vf_token', access_token);
    setToken(access_token);
    setUser(userData);
    return { success: true, user: userData };
  };

  const verify2FA = async (email, code) => {
    const response = await api.post('/auth/verify-2fa', { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('vf_token', access_token);
    setToken(access_token);
    setUser(userData);
    return { success: true, user: userData };
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await api.post('/auth/verify-email', { email, code });
    return response.data;
  };

  const staffPinLogin = async (shopId, pin) => {
    const response = await api.post('/auth/staff-pin-login', { 
      shop_id: shopId, 
      pin: pin 
    });
    const { access_token, staff } = response.data;
    localStorage.setItem('vf_token', access_token);
    setToken(access_token);
    setUser({
      ...staff,
      role: staff.role,
      is_staff: true
    });
    return { success: true, staff };
  };

  const forgotPassword = async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  };

  const resetPassword = async (email, code, newPassword) => {
    const response = await api.post('/auth/reset-password', { 
      email, 
      code, 
      new_password: newPassword 
    });
    return response.data;
  };

  const loginWithToken = (userData, accessToken) => {
    localStorage.setItem('vf_token', accessToken);
    setToken(accessToken);
    setUser(userData);
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('vf_token');
    setToken(null);
    setUser(null);
  };

  // Role checks
  const isSuperAdmin = () => user?.role === 'super_admin';
  const isShopAdmin = () => user?.role === 'shop_admin';
  const isInvoiceUser = () => user?.role === 'invoice_user';
  const isManager = () => user?.role === 'manager';
  const isCashier = () => user?.role === 'cashier';
  const isStockClerk = () => user?.role === 'stock_clerk';
  const isDriver = () => user?.role === 'driver';
  const isStaff = () => ['manager', 'cashier', 'stock_clerk', 'driver'].includes(user?.role);

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    logout,
    register,
    verifyEmail,
    verify2FA,
    staffPinLogin,
    forgotPassword,
    resetPassword,
    isSuperAdmin,
    isShopAdmin,
    isInvoiceUser,
    isManager,
    isCashier,
    isStockClerk,
    isDriver,
    isStaff,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
